import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Activity, MessageSquare, Send, ShieldCheck, Eye, Pencil } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Badge } from "@/components/kit";

const ROLES = ["owner", "executive_chef", "chef", "sous_chef", "prep_chef", "bartender", "viewer"];
const MOD_LABELS = { menus: "Menus", pca: "Cost / PCA", purchasing: "Purchasing", kitchen: "Kitchen", team: "Team" };

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "chef" });
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [perms, setPerms] = useState(null);
  const [permMeta, setPermMeta] = useState({ modules: [], roles: [], can_edit: false });
  const [savingPerms, setSavingPerms] = useState(false);
  const canManage = ["admin", "owner", "executive_chef", "master_chef"].includes(user?.role);
  const load = () => {
    api.get("/team").then(({ data }) => setData(data)).catch(() => setData({ members: [], activity: [] }));
    api.get("/team/messages").then(({ data }) => setMessages(data.messages || [])).catch(() => {});
    api.get("/team/permissions").then(({ data }) => { setPerms(data.perms); setPermMeta({ modules: data.modules, roles: data.roles, can_edit: data.can_edit }); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    try { const { data } = await api.post("/team/invite", form); toast.success(`${data.message} Temp password: ${data.temp_password}`); setShow(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const changeRole = async (id, role) => {
    try { await api.put(`/team/members/${id}/role`, { role }); toast.success("Role updated."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const sendMsg = async () => {
    if (!msg.trim()) return;
    try { await api.post("/team/messages", { text: msg }); setMsg(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const togglePerm = (role, mod, action) => {
    setPerms((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const cur = next[role][mod][action];
      next[role][mod][action] = !cur;
      if (action === "edit" && !cur) next[role][mod].view = true;
      if (action === "view" && cur) next[role][mod].edit = false;
      return next;
    });
  };
  const savePerms = async () => {
    setSavingPerms(true);
    try { await api.put("/team/permissions", { perms }); toast.success("Role permissions saved."); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingPerms(false); }
  };
  if (!data) return <Loader />;

  return (
    <div>
      <PageHeader eyebrow="Kitchen Brigade" title="Team" subtitle="Roles, permissions and the activity feed for your kitchen."
        action={<button onClick={() => setShow(true)} data-testid="invite-btn" className="btn-gold px-6 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2"><Plus size={14} /> Invite Member</button>} />

      {show && (
        <div className="matte-card p-6 mb-6 grid sm:grid-cols-4 gap-3">
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60" />
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60">{ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}</select>
          <button onClick={invite} data-testid="invite-submit" className="btn-gold px-6 text-xs uppercase tracking-widest rounded-sm">Send Invite</button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="matte-card p-6">
          <p className="label-eyebrow mb-4 flex items-center gap-2"><Users size={14} className="text-gold" /> Members ({data.members.length})</p>
          <div className="divide-y divide-white/5">
            {data.members.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-3.5">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold font-semibold">{(m.name || "?")[0].toUpperCase()}</div>
                  <div><p className="text-white/90">{m.name}</p><p className="text-white/40 text-xs">{m.email}</p></div>
                </div>
                {canManage && m.role !== "admin" && m.id !== user?.id
                  ? <select value={m.role} onChange={(e) => changeRole(m.id, e.target.value)} data-testid={`role-select-${m.id}`}
                      className="bg-surface border border-white/10 rounded-sm px-2 py-1.5 text-white/80 text-xs outline-none focus:border-gold/60">{ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}</select>
                  : <Badge tone={m.role === "admin" ? "gold" : "muted"}>{m.role.replace("_", " ")}</Badge>}
              </div>
            ))}
          </div>
        </div>
        <div className="matte-card p-6">
          <p className="label-eyebrow mb-4 flex items-center gap-2"><Activity size={14} className="text-gold" /> Activity Feed</p>
          {data.activity.length === 0 && <p className="text-white/40 text-sm">No activity yet.</p>}
          <div className="space-y-3">
            {data.activity.map((a) => (
              <div key={a.id} className="text-sm text-white/70 flex gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-gold mt-2 shrink-0" />
                <div><span className="text-gold">{a.actor}</span> {a.text}<p className="text-white/30 text-xs">{new Date(a.created_at).toLocaleString()}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {perms && permMeta.roles.length > 0 && (
        <div className="matte-card p-6 mt-4" data-testid="role-permissions">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
            <p className="label-eyebrow flex items-center gap-2"><ShieldCheck size={14} className="text-gold" /> Role Permissions</p>
            {permMeta.can_edit && (
              <button onClick={savePerms} disabled={savingPerms} data-testid="save-permissions-btn" className="btn-gold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm">
                {savingPerms ? "Saving…" : "Save Permissions"}
              </button>
            )}
          </div>
          <p className="text-white/45 text-sm mb-5">Fine-tune what each role can <span className="inline-flex items-center gap-1"><Eye size={12} /> view</span> and <span className="inline-flex items-center gap-1"><Pencil size={12} /> edit</span>. Owners &amp; executive chefs always have full access.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left label-eyebrow border-b border-white/10">
                  <th className="py-2 pr-4 font-medium">Role</th>
                  {permMeta.modules.map((m) => <th key={m} className="py-2 px-3 text-center font-medium">{MOD_LABELS[m] || m}</th>)}
                </tr>
              </thead>
              <tbody>
                {permMeta.roles.map((role) => (
                  <tr key={role} className="border-b border-white/5">
                    <td className="py-3 pr-4 capitalize text-white/85">{role.replace(/_/g, " ")}</td>
                    {permMeta.modules.map((m) => {
                      const cell = perms[role]?.[m] || { view: false, edit: false };
                      return (
                        <td key={m} className="py-3 px-3">
                          <div className="flex items-center justify-center gap-3">
                            <label className="flex items-center gap-1.5 cursor-pointer text-white/60" title="Can view">
                              <input type="checkbox" checked={cell.view} disabled={!permMeta.can_edit} data-testid={`perm-${role}-${m}-view`}
                                onChange={() => togglePerm(role, m, "view")} className="accent-[#D4AF37]" />
                              <Eye size={13} />
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer text-white/60" title="Can edit">
                              <input type="checkbox" checked={cell.edit} disabled={!permMeta.can_edit} data-testid={`perm-${role}-${m}-edit`}
                                onChange={() => togglePerm(role, m, "edit")} className="accent-[#D4AF37]" />
                              <Pencil size={13} />
                            </label>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="matte-card p-6 mt-4">
        <p className="label-eyebrow mb-4 flex items-center gap-2"><MessageSquare size={14} className="text-gold" /> Team Messages</p>
        <div className="max-h-72 overflow-y-auto space-y-3 mb-4">
          {messages.length === 0 && <p className="text-white/40 text-sm">No messages yet. Start the conversation.</p>}
          {messages.map((m) => (
            <div key={m.id} className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold text-xs font-semibold shrink-0">{(m.author || "?")[0].toUpperCase()}</div>
              <div className="min-w-0">
                <p className="text-sm"><span className="text-gold">{m.author}</span> <span className="text-white/30 text-xs">{new Date(m.created_at).toLocaleString()}</span></p>
                <p className="text-white/75 text-sm">{m.text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="team-message-input" placeholder="Message your team…"
            onKeyDown={(e) => e.key === "Enter" && sendMsg()}
            className="flex-1 bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60 text-sm" />
          <button onClick={sendMsg} data-testid="team-message-send" className="btn-gold px-5 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2"><Send size={14} /></button>
        </div>
      </div>
    </div>
  );
}
